import { NEVER, Observable, of, throwError } from 'rxjs'
import { delay } from 'rxjs/operators'
import { ApiService } from 'src/app/services/api.service'
import { AccountService } from 'src/app/account/services/account.service'
import { AccessLevelService } from 'src/app/services/access-level.service'
import { ApplicationProviderService } from 'src/app/account/services/application.service'
import { UsageBillingService } from 'src/app/account/components/usage-billing/usage-billing.service'
import { StacksetsRegenerationPollService } from 'src/app/shared/services/stacksets-regeneration-poll.service'
import { JobDto, JobStatus } from 'src/app/services/dtos'
import { HttpError } from 'src/utils/errors'
import { MOCK_TEST_ACCOUNT_ID, TestPageMockState } from './showcase-mock-state'
import { buildMockAccessTemplates, buildMockSyncPairAccess } from './mocks/showcase-mock-core'
import { buildMockFirewallEvents } from './mocks/showcase-modals-mocks'
import { buildMockRetentionSettings } from './mocks/showcase-retention-mocks'
import { buildMockJobDto } from './mocks/showcase-stacksets-mocks'

/** The singletons the showcase patches. */
export interface ShowcasePatchDeps {
  apiService: ApiService
  accountService: AccountService
  accessLevelService: AccessLevelService
  applicationProviderService: ApplicationProviderService
  usageBillingService: UsageBillingService
  regenerationPollService: StacksetsRegenerationPollService
}

/**
 * Originals captured at install time, restored on destroy so the patches don't
 * leak into other routes.
 */
export interface ShowcasePatchedOriginals {
  entitledForCapability?: typeof AccountService.prototype.entitledForCapability
  getUserPermissions?: typeof AccessLevelService.prototype.getUserPermissions
  getAccessTemplates?: typeof ApiService.prototype.getAccessTemplates
  testSyncPairAccess?: typeof ApiService.prototype.testSyncPairAccess
  testSingleSyncPairAccess?: typeof ApiService.prototype.testSingleSyncPairAccess
  fetchFirewallLogs?: typeof ApiService.prototype.fetchFirewallLogs
  getJob?: typeof ApiService.prototype.getJob
  getNetworkSandboxBlockedIps?: typeof ApiService.prototype.getNetworkSandboxBlockedIps
  getNetworkSandboxBlockedDomains?: typeof ApiService.prototype.getNetworkSandboxBlockedDomains
  getSyncPairResourceConfig?: typeof ApiService.prototype.getSyncPairResourceConfig
  setSyncPairResourceConfig?: typeof ApiService.prototype.setSyncPairResourceConfig
  getResourceTypeConfigs?: typeof ApiService.prototype.getResourceTypeConfigs
  setResourceTypeConfigs?: typeof ApiService.prototype.setResourceTypeConfigs
  getSyncPairResourceConfigs?: typeof ApiService.prototype.getSyncPairResourceConfigs
  setSyncPairResourceConfigs?: typeof ApiService.prototype.setSyncPairResourceConfigs
  loadDashboard?: typeof UsageBillingService.prototype.loadDashboard
  loadDailyForCycle?: typeof UsageBillingService.prototype.loadDailyForCycle
  loadAppBreakdownForCycles?: typeof UsageBillingService.prototype.loadAppBreakdownForCycles
  loadLastBackupBreakdown?: typeof UsageBillingService.prototype.loadLastBackupBreakdown
  getRetentionSettings?: typeof ApiService.prototype.getRetentionSettings
  setRetentionSettings?: typeof ApiService.prototype.setRetentionSettings
  listRecoveryPoints$?: typeof ApiService.prototype.listRecoveryPoints$
  updateApplicationRecoveryPoint?: typeof ApiService.prototype.updateApplicationRecoveryPoint
  loadApplication$?: typeof ApplicationProviderService.prototype.loadApplication$
  accountIdStream?: Observable<string>
  /** The real accountDto, displaced so the showcase only ever addresses the mock account. */
  accountDto?: any
  accountDtoPatched?: boolean
  entitlementPatched?: boolean
}

/**
 * Singleton patches that make the showcase work without a real backend.
 *
 * These stay page-level rather than per-section: three of them have genuine
 * cross-section value conflicts (the access patches serve both legacy-templates
 * and the modals update-access dialog; getJob serves stacksets, legacy-templates
 * and modals; entitledForCapability must be off for sandbox scenarios and on for
 * everything else), and per-section install/restore can't nest correctly when
 * sections mount in arbitrary click order. Every mutable value they read lives on
 * TestPageMockState, which carries its own defaults.
 */
export function installShowcasePatches(deps: ShowcasePatchDeps, state: TestPageMockState): ShowcasePatchedOriginals {
  const { apiService, accountService, accessLevelService, applicationProviderService, usageBillingService } = deps
  const originals: ShowcasePatchedOriginals = {}

  // Capability flags default ON so dialogs render every conditional step
  // (quarantine, sandbox, advanced sandbox, RBAC, app-group restore) regardless
  // of the dev account's real entitlements.
  originals.entitledForCapability = accountService.entitledForCapability.bind(accountService)
  ;(accountService as any).entitledForCapability = (capability: string) =>
    !state.mockDisabledCapabilities.has(capability)

  // Point every account-scoped consumer at the mock account, unconditionally. A real
  // accountDto left in place (from a route the reviewer arrived from) would make the
  // showcase address that account, and UsageBillingComponent also bails outright on a
  // falsy accountId. Captured and restored so navigating away is a no-op.
  originals.accountDto = (accountService as any).accountDto
  originals.accountDtoPatched = true
  ;(accountService as any).accountDto = { accountId: MOCK_TEST_ACCOUNT_ID }

  // The dashboard reads Flex off the entitlement, not the view model, and 404s a
  // non-Flex account — so the preview needs this to render at all.
  state.originalEntitlementDto = (accountService as any).entitlementDto
  originals.entitlementPatched = true
  state.applyScenarioEntitlement()

  // Usage & Billing renders the real component, which fetches its whole view model
  // through this one call. NEVER emitting (loading) and erroring are scenarios in
  // their own right, so the patch reads the active scenario each time rather than
  // closing over a fixed value.
  originals.loadDashboard = usageBillingService.loadDashboard.bind(usageBillingService)
  ;(usageBillingService as any).loadDashboard = () => {
    const scenario = state.usageBillingActiveScenario
    if (scenario.lifecycle === 'loading') return NEVER
    if (scenario.lifecycle === 'error') return throwError(() => new Error('Mock usage report failure'))
    return of(scenario.vm)
  }

  // Selecting a period that reaches past the scenario's cycle triggers a real
  // per-cycle fetch. Each scenario's VM already carries every day it means to show,
  // so this answers empty rather than letting /test_page hit the live endpoint.
  originals.loadDailyForCycle = usageBillingService.loadDailyForCycle.bind(usageBillingService)
  ;(usageBillingService as any).loadDailyForCycle = () => of([])
  originals.loadAppBreakdownForCycles = usageBillingService.loadAppBreakdownForCycles.bind(usageBillingService)
  ;(usageBillingService as any).loadAppBreakdownForCycles = () => of([])
  // Last backup stands in with the scenario's newest breakdown day.
  originals.loadLastBackupBreakdown = usageBillingService.loadLastBackupBreakdown.bind(usageBillingService)
  ;(usageBillingService as any).loadLastBackupBreakdown = () =>
    of(state.usageBillingActiveScenario.vm?.dailyBreakdown.at(-1) ?? null)

  // Treat the test-page user as a legacy admin so RBAC permission filtering doesn't
  // drop the mock applications out of the dialog candidate lists. This also satisfies
  // Usage & Billing's readBillableResourceReport guard.
  originals.getUserPermissions = accessLevelService.getUserPermissions.bind(accessLevelService)
  ;(accessLevelService as any).getUserPermissions = () =>
    of({ isLegacyAdmin: true, appPermissionsMap: new Map(), accountPermissions: new Set() } as any)

  // Stub the API calls update-access-dialog makes so it renders with populated
  // content instead of skeleton rows. The StackSets block is opt-in: it is what
  // UpdateAccessDialogComponent derives its own stackSetsEnabled from, so including
  // it unconditionally would turn that dialog's preview into a StackSets one.
  originals.getAccessTemplates = apiService.getAccessTemplates.bind(apiService)
  ;(apiService as any).getAccessTemplates = () =>
    of(
      buildMockAccessTemplates({
        includeStackSets: state.legacyTemplateFlow === 'stacksets',
        includeAzure: state.legacyTemplateIsAzure,
      })
    )
  originals.testSyncPairAccess = apiService.testSyncPairAccess.bind(apiService)
  ;(apiService as any).testSyncPairAccess = () =>
    of(
      buildMockSyncPairAccess({
        managedByStackSet: state.legacyTemplateFlow === 'stacksets',
        unknownVersion: state.legacyTemplateIsAzure,
      })
    )
  // Every access card's TEST ACCESS button goes through this one, not
  // testSyncPairAccess. Unpatched it would fire a real request for the mock
  // account IDs and flip the card into an unrelated error state.
  originals.testSingleSyncPairAccess = apiService.testSingleSyncPairAccess.bind(apiService)
  ;(apiService as any).testSingleSyncPairAccess = () =>
    of(
      buildMockSyncPairAccess({
        managedByStackSet: state.legacyTemplateFlow === 'stacksets',
        unknownVersion: state.legacyTemplateIsAzure,
      })
    )

  // Sandbox event log: the real API rejects mock IDs, so without this stub the
  // dialog would always render the "failed to load" state.
  originals.fetchFirewallLogs = apiService.fetchFirewallLogs.bind(apiService)
  ;(apiService as any).fetchFirewallLogs = () =>
    of({
      events: state.mockFirewallEventsPopulated ? buildMockFirewallEvents() : [],
      awsConsoleUrl: 'https://console.aws.amazon.com/cloudwatch/',
    })

  // application-test-config-dialog fetches recently-blocked IPs/domains to populate
  // its "Suggested" lists when advanced sandbox is on. The real endpoints reject the
  // mock app IDs.
  originals.getNetworkSandboxBlockedIps = apiService.getNetworkSandboxBlockedIps.bind(apiService)
  ;(apiService as any).getNetworkSandboxBlockedIps = () =>
    of({
      status: 'ok',
      results: [
        { endpoint: '203.0.113.24', count: 12 },
        { endpoint: '198.51.100.7', count: 5 },
      ],
    })
  originals.getNetworkSandboxBlockedDomains = apiService.getNetworkSandboxBlockedDomains.bind(apiService)
  ;(apiService as any).getNetworkSandboxBlockedDomains = () =>
    of({
      status: 'ok',
      results: [
        { endpoint: 'telemetry.example.com', count: 9 },
        { endpoint: 'updates.vendor.io', count: 3 },
      ],
    })

  // The S3 bucket / blob-container issue components fetch a single resource config in
  // their ngOnInit. They prefer the real SyncPair over the mock Application, so an
  // unpatched call fires against a fake endpoint pair and raises an error toast the
  // moment the Issues showcase is expanded. delay(0) keeps the round-trip ordering the
  // finalize-nulls-the-subscription shape in those components expects.
  originals.getSyncPairResourceConfig = apiService.getSyncPairResourceConfig.bind(apiService)
  ;(apiService as any).getSyncPairResourceConfig = () => of(null).pipe(delay(0))

  // The same issue components write that config back from a live submit button, and
  // the Issues showcase has no click blocker. Unpatched, saving one issued a real PUT
  // against whichever account the showcase is pointed at, keyed by a mock ARN.
  originals.setSyncPairResourceConfig = apiService.setSyncPairResourceConfig.bind(apiService)
  ;(apiService as any).setSyncPairResourceConfig = (
    _accountId: string,
    _sourceEndpointUri: string,
    _targetEndpointUri: string,
    resourceArn: string,
    resourceConfig: unknown
  ) => of({ arn: resourceArn, resourceConfig } as any).pipe(delay(0))

  // Account-level resource type configs: read on mount by the bucket-name-conflict
  // issue, written back from its form.
  originals.getResourceTypeConfigs = apiService.getResourceTypeConfigs.bind(apiService)
  ;(apiService as any).getResourceTypeConfigs = () => of({ resourceTypeConfigs: [] } as any).pipe(delay(0))
  originals.setResourceTypeConfigs = apiService.setResourceTypeConfigs.bind(apiService)
  ;(apiService as any).setResourceTypeConfigs = (_accountId: string, configs: unknown) =>
    of(configs as any).pipe(delay(0))

  // unquarantine-dialog loads existing resource configs on open and writes them back
  // on UNQUARANTINE. Stub the read with no configs (the dialog builds defaults for
  // anything missing) and echo the write back so the optimistic status update runs.
  originals.getSyncPairResourceConfigs = apiService.getSyncPairResourceConfigs.bind(apiService)
  ;(apiService as any).getSyncPairResourceConfigs = () => of([])
  originals.setSyncPairResourceConfigs = apiService.setSyncPairResourceConfigs.bind(apiService)
  ;(apiService as any).setSyncPairResourceConfigs = (_accountId: string, _syncPair: unknown, configs: unknown) =>
    of(configs)

  // Retention policy screen. The account-id stream stays synchronous — production's
  // ReplaySubject already holds a value and emits on subscribe. The endpoints below
  // must NOT: the component assigns each subscription after subscribing while a
  // finalize() nulls it on completion, so a stub that completes synchronously leaves
  // the "loading" flag stuck on and the form hidden behind a spinner forever.
  // delay(0) restores the round-trip ordering a real request has.
  originals.accountIdStream = accountService.accountIdStream
  accountService.accountIdStream = of(MOCK_TEST_ACCOUNT_ID)
  originals.getRetentionSettings = apiService.getRetentionSettings.bind(apiService)
  ;(apiService as any).getRetentionSettings = () => of(buildMockRetentionSettings()).pipe(delay(0))
  originals.setRetentionSettings = apiService.setRetentionSettings.bind(apiService)
  ;(apiService as any).setRetentionSettings = () => of(buildMockRetentionSettings()).pipe(delay(0))
  originals.listRecoveryPoints$ = apiService.listRecoveryPoints$.bind(apiService)
  ;(apiService as any).listRecoveryPoints$ = () => of([...state.mockRetainedRps]).pipe(delay(0))
  // Release drops the selected points from the mock list so the flow ends in the
  // state production would reach; "Reset mock data" restores them.
  originals.updateApplicationRecoveryPoint = apiService.updateApplicationRecoveryPoint.bind(apiService)
  ;(apiService as any).updateApplicationRecoveryPoint = (
    _accountId: string,
    _applicationId: string,
    recoveryPointId: string
  ) => {
    state.mockRetainedRps = state.mockRetainedRps.filter((rp) => rp.recoveryPointId !== recoveryPointId)
    return of(null).pipe(delay(0))
  }
  // Scoped to the retention mock app ids only — every other caller (dialog candidate
  // lists, screen previews) keeps the real cache-backed loader.
  originals.loadApplication$ = applicationProviderService.loadApplication$.bind(applicationProviderService)
  ;(applicationProviderService as any).loadApplication$ = (
    applicationId: string,
    accountId: string,
    refreshDto?: boolean,
    forceRefreshResources?: boolean
  ) =>
    state.mockRetentionApps.has(applicationId)
      ? of(state.mockRetentionApps.get(applicationId))
      : originals.loadApplication$(applicationId, accountId, refreshDto, forceRefreshResources)

  // StackSets regen-job poll: drive the gating in stacksets-instructions and
  // stacksets-update-instructions through every status sequence the production
  // components handle.
  originals.getJob = apiService.getJob.bind(apiService)
  ;(apiService as any).getJob = (accountId: string, jobId: string): Observable<JobDto> => {
    const mk = (status: JobStatus) => buildMockJobDto(jobId, status, accountId)
    const elapsed = Date.now() - state.stacksetsScenarioStartMs
    switch (state.stacksetsJobScenario) {
      case 'instant_done':
        return of(mk('done'))
      case 'skipped':
        return of(mk('skipped'))
      case 'running_then_done':
        return of(elapsed < 10_000 ? mk('running') : mk('done'))
      case 'running_then_failed':
        // Return failed on the first poll — running_timeout already covers the long-running case.
        return of(mk('failed'))
      case 'running_timeout':
        return of(mk('running'))
      case 'http_error':
        return throwError(
          () => new HttpError(500, 'mock failure', '/api/accounts/test/jobs/' + jobId, { message: 'Mock 500' })
        )
      case 'none':
      default:
        return of(mk('done'))
    }
  }

  return originals
}

/** Restores every patched singleton. No-op for anything install never reached. */
export function restoreShowcasePatches(
  deps: ShowcasePatchDeps,
  state: TestPageMockState,
  originals: ShowcasePatchedOriginals
): void {
  const {
    apiService,
    accountService,
    accessLevelService,
    applicationProviderService,
    usageBillingService,
    regenerationPollService,
  } = deps

  if (originals.entitledForCapability) accountService.entitledForCapability = originals.entitledForCapability
  if (originals.getUserPermissions) accessLevelService.getUserPermissions = originals.getUserPermissions
  if (originals.loadDashboard) usageBillingService.loadDashboard = originals.loadDashboard
  if (originals.loadDailyForCycle) usageBillingService.loadDailyForCycle = originals.loadDailyForCycle
  if (originals.loadAppBreakdownForCycles) {
    usageBillingService.loadAppBreakdownForCycles = originals.loadAppBreakdownForCycles
  }
  if (originals.loadLastBackupBreakdown) {
    usageBillingService.loadLastBackupBreakdown = originals.loadLastBackupBreakdown
  }
  if (originals.accountDtoPatched) (accountService as any).accountDto = originals.accountDto
  if (originals.entitlementPatched) (accountService as any).entitlementDto = state.originalEntitlementDto
  if (originals.getAccessTemplates) apiService.getAccessTemplates = originals.getAccessTemplates
  if (originals.testSyncPairAccess) apiService.testSyncPairAccess = originals.testSyncPairAccess
  if (originals.testSingleSyncPairAccess) apiService.testSingleSyncPairAccess = originals.testSingleSyncPairAccess
  if (originals.fetchFirewallLogs) apiService.fetchFirewallLogs = originals.fetchFirewallLogs
  if (originals.getJob) apiService.getJob = originals.getJob
  if (originals.getNetworkSandboxBlockedIps) {
    apiService.getNetworkSandboxBlockedIps = originals.getNetworkSandboxBlockedIps
  }
  if (originals.getNetworkSandboxBlockedDomains) {
    apiService.getNetworkSandboxBlockedDomains = originals.getNetworkSandboxBlockedDomains
  }
  if (originals.getSyncPairResourceConfig) {
    apiService.getSyncPairResourceConfig = originals.getSyncPairResourceConfig
  }
  if (originals.setSyncPairResourceConfig) {
    apiService.setSyncPairResourceConfig = originals.setSyncPairResourceConfig
  }
  if (originals.getResourceTypeConfigs) {
    apiService.getResourceTypeConfigs = originals.getResourceTypeConfigs
  }
  if (originals.setResourceTypeConfigs) {
    apiService.setResourceTypeConfigs = originals.setResourceTypeConfigs
  }
  if (originals.getSyncPairResourceConfigs) {
    apiService.getSyncPairResourceConfigs = originals.getSyncPairResourceConfigs
  }
  if (originals.setSyncPairResourceConfigs) {
    apiService.setSyncPairResourceConfigs = originals.setSyncPairResourceConfigs
  }
  if (originals.getRetentionSettings) apiService.getRetentionSettings = originals.getRetentionSettings
  if (originals.setRetentionSettings) apiService.setRetentionSettings = originals.setRetentionSettings
  if (originals.listRecoveryPoints$) apiService.listRecoveryPoints$ = originals.listRecoveryPoints$
  if (originals.updateApplicationRecoveryPoint) {
    apiService.updateApplicationRecoveryPoint = originals.updateApplicationRecoveryPoint
  }
  if (originals.loadApplication$) applicationProviderService.loadApplication$ = originals.loadApplication$
  if (originals.accountIdStream) accountService.accountIdStream = originals.accountIdStream

  // Restore the real regen poll timeout so navigating away leaves the singleton untouched.
  regenerationPollService.resetTimeoutMsForTesting()
}
